// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Workspace utilities - provides centralized functions for working with organizations and workspaces
 */
// Default organization ID for the application
export const DEFAULT_ORG_ID = 'umd-org';

// cm-coding: Impersonation changes the effective user in the same browser.
// Scope navigation preferences by user so one identity cannot overwrite the
// workspace that another identity should return to.
export const STORAGE_KEY_SELECTED_WORKSPACE = 'selected_workspace';

/**
 * Interface for the workspace selection object stored in localStorage
 */
export interface WorkspaceSelection {
  orgId: string;
  wsId: string;
}

/**
 * Get the user's active workspace selection
 * This centralizes all the logic for retrieving the active workspace
 */
function activeWorkspaceStorageKey(userId: string): string {
  return `${STORAGE_KEY_SELECTED_WORKSPACE}:${userId}`;
}

export function getActiveWorkspace(userId: string): WorkspaceSelection | null {
  try {
    const storageKey = activeWorkspaceStorageKey(userId);
    let savedSelection = localStorage.getItem(storageKey);
    // cm-coding: Older releases stored one browser-wide selection. Move that
    // value once for the signed-in user, then delete the old key so later
    // impersonated identities cannot inherit it.
    if (!savedSelection) {
      savedSelection = localStorage.getItem(STORAGE_KEY_SELECTED_WORKSPACE);
      if (savedSelection) {
        localStorage.setItem(storageKey, savedSelection);
        localStorage.removeItem(STORAGE_KEY_SELECTED_WORKSPACE);
      }
    }
    if (savedSelection) {
      return JSON.parse(savedSelection) as WorkspaceSelection;
    }
    return null;
  } catch (error) {
    console.error('[WorkspaceUtils] Error retrieving active workspace:', error);
    return null;
  }
}

/**
 * Save the user's active workspace selection
 */
export function saveActiveWorkspace(userId: string, orgId: string, wsId: string): void {
  try {
    localStorage.setItem(
      activeWorkspaceStorageKey(userId),
      JSON.stringify({ orgId, wsId }),
    );
    localStorage.removeItem(STORAGE_KEY_SELECTED_WORKSPACE);
    console.log(`[WorkspaceUtils] Saved active workspace: org=${orgId}, ws=${wsId}`);
  } catch (error) {
    console.error('[WorkspaceUtils] Error saving active workspace:', error);
  }
}

/**
 * Clear the user's active workspace selection
 */
export function clearActiveWorkspace(userId: string): void {
  localStorage.removeItem(activeWorkspaceStorageKey(userId));
  console.log('[WorkspaceUtils] Cleared active workspace selection');
}

// cm-coding: HashRouter places the application query after '#'. The browser's
// normal search string contains only pre-router values such as CAS parameters.
export function getUrlWorkspace(): WorkspaceSelection | null {
  const queryIndex = window.location.hash.indexOf('?');
  if (queryIndex === -1) return null;

  const params = new URLSearchParams(window.location.hash.slice(queryIndex + 1));
  const orgId = params.get('org');
  const wsId = params.get('ws');
  return orgId && wsId ? { orgId, wsId } : null;
}

// cm-coding: Workspace, Vault, and tab parameters belong to the current
// identity. Keeping them across impersonation can navigate the next identity
// back to inaccessible or unrelated data instead of its saved workspace.
export function clearWorkspaceRouteParams(): void {
  const queryIndex = window.location.hash.indexOf('?');
  if (queryIndex === -1) return;

  const route = window.location.hash.slice(0, queryIndex);
  const params = new URLSearchParams(window.location.hash.slice(queryIndex + 1));
  params.delete('org');
  params.delete('ws');
  params.delete('vault');
  params.delete('tab');

  const remainingQuery = params.toString();
  const url = new URL(window.location.href);
  url.hash = remainingQuery ? `${route}?${remainingQuery}` : route;
  window.history.replaceState({}, document.title, url.toString());
}
